<script setup>
  import { computed, useTemplateRef } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useElementSize } from '@vueuse/core';
  import BaseModal from '@/components/base/BaseModal.vue';
  import WidgetEditorGallery from '@/components/WidgetEditorGallery.vue';
  import WidgetEditorPreview from '@/components/WidgetEditorPreview.vue';
  import WidgetEditorSettings from '@/components/WidgetEditorSettings.vue';
  import { useWidgetStore } from '@/stores/WidgetStore';

  const widgetStore = useWidgetStore();

  // From this width the panels sit side by side (widget-editor.scss, same breakpoint)
  const EDITOR_WIDE_MIN_WIDTH = 1160;

  const { t } = useI18n();

  const editorElement = useTemplateRef('editor-element');

  const { width: editorWidth } = useElementSize(editorElement, undefined, { box: 'border-box' });

  // Open while a widget is being customized; closing (X, outside click) clears it
  const isOpen = computed({
    get: () => widgetStore.activeWidgetId !== null,
    set: (open) => {
      if (!open) widgetStore.closeEditor();
    }
  });

  // Rendered rather than hidden with CSS, so the sidebar's live thumbnails only load when shown
  const galleryLayout = computed(() => editorWidth.value >= EDITOR_WIDE_MIN_WIDTH ? 'cards' : 'row');
</script>

<template>
  <!-- Stays mounted and toggles v-model, so the sheet's leave animation can play -->
  <BaseModal
    v-model="isOpen"
    variant="sheet"
    closeButton="done"
    role="dialog"
    aria-modal="true"
    aria-labelledby="widget-editor-title"
  >
    <header class="jwx-modal__header">
      <h2 id="widget-editor-title" class="jwx-modal__title">{{ t('widgets.editorTitle') }}</h2>
    </header>
    <div ref="editor-element" class="jwx-modal__body jwx-widget-editor">
      <WidgetEditorGallery :layout="galleryLayout" />
      <WidgetEditorPreview v-if="widgetStore.embeds[widgetStore.activeWidgetId]" />
      <WidgetEditorSettings v-if="widgetStore.embeds[widgetStore.activeWidgetId]" />
    </div>
  </BaseModal>
</template>
